import {weakMemo} from '@packages/utilities/memo';
import {getName, getPluginName} from '@packages/systems/wfdl-Qualified';
import {Plugins, SiteModules} from '@packages/systems/wfdl';
import {PackageJson} from 'type-fest';
import {z} from 'zod/v4';
import semver from 'semver';
import {SANDBOX_MANAGED_DEPENDENCIES, WEBFLOW_DEPENDENCIES} from '../constants';
import {isValidNpmPackageName} from '../build-tools/isValidNpmPackageName';

const WEBFLOW_DEPENDENCIES_SET = new Set(WEBFLOW_DEPENDENCIES);

const PackageJsonSchema = z.object({
  dependencies: z.record(z.string(), z.string()),
});

/**
 * Parses a `package.json` source string into a dependency map, dropping
 * any key that doesn't pass `isValidNpmPackageName` (APAI-3268). Returns
 * `null` only on outright JSON / schema failure — invalid keys are
 * silently filtered so a single bad entry written by the LLM doesn't
 * poison the Designer's `siteModuleDependencies` map and cause repeated
 * `bun add` failures on subsequent builds.
 */
export const projectDependenciesFromPackageJson = (
  source: string
): Record<string, {version: string}> | null => {
  // JSON.parse throws on malformed input. Since LLM tools can now write
  // package.json directly (APAI-3268), we have to treat parse failure the
  // same as a schema mismatch — return null and let the caller keep using
  // its prior dependency map.
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch {
    return null;
  }
  const result = PackageJsonSchema.safeParse(parsed);
  if (!result.success) return null;
  const deps: Record<string, {version: string}> = {};
  for (const [name, version] of Object.entries(result.data.dependencies)) {
    if (!isValidNpmPackageName(name)) continue;
    deps[name] = {version};
  }
  return deps;
};

/**
 * Non-range versions (`next`, `file:`, `latest`) are intentional;
 * bun update must not rewrite them to a version range.
 */
export const isPinnedVersion = (version: string): boolean =>
  semver.validRange(version) === null;

/**
 * Keep any existing @webflow entry. Overlay only fills missing names;
 * bun update is what moves the range.
 */
export const shouldKeepExistingWebflowDependency = (
  packageName: string,
  existing: SiteModules.SiteModuleDependencyType | undefined
): boolean =>
  existing !== undefined && WEBFLOW_DEPENDENCIES_SET.has(packageName);

/**
 * Image `@webflow/*` must be copied here too: publish `writeTree` overwrites
 * `/package.json`, and persisted site data may omit them.
 * Names the image does not pin stay omitted.
 */
export const getManagedSandboxDependencies = (
  baseDependencies: SiteModules.SiteModuleDependencyMap | null
): SiteModules.SiteModuleDependencyMap =>
  Object.fromEntries(
    [...SANDBOX_MANAGED_DEPENDENCIES, ...WEBFLOW_DEPENDENCIES].flatMap(
      (name) => {
        const dependency = baseDependencies?.[name];
        return dependency ? [[name, dependency]] : [];
      }
    )
  );

export const overlayManagedSandboxDependencies = (
  existing: SiteModules.SiteModuleDependencyMap,
  managed: SiteModules.SiteModuleDependencyMap
): SiteModules.SiteModuleDependencyMap => {
  const next: SiteModules.SiteModuleDependencyMap = {...existing};
  for (const [packageName, dependency] of Object.entries(managed)) {
    if (shouldKeepExistingWebflowDependency(packageName, next[packageName])) {
      continue;
    }
    next[packageName] = dependency;
  }
  return next;
};

/**
 * Projects site module dependencies to a package.json source string.
 *
 * This projector converts the QualifiedMap of site module dependencies
 * into a properly formatted package.json that can be used by the remote
 * sandbox to resolve npm dependencies.
 *
 * Filters out keys that fail `isValidNpmPackageName` as belt-and-suspenders:
 * `projectDependenciesFromPackageJson` is the primary gate, but if a stale
 * `siteModuleDependencies` entry slipped through (e.g. persisted before this
 * fix shipped), we don't want to re-write it to the sandbox.
 */
export const projectDependenciesToPackageJson = weakMemo(
  (
    siteModuleDependencies: Plugins.PluginsType['siteModuleDependencies']
  ): string => {
    const dependencies: Record<string, string> = {};
    siteModuleDependencies.forEach((dep, qualifiedName) => {
      if (getPluginName(qualifiedName) !== Plugins.name('__SitePlugin')) return;
      const packageName: string = getName(qualifiedName);
      if (!isValidNpmPackageName(packageName)) return;
      // @webflow dependencies should be in caret range format
      // so that they can be updated to the latest compatible version
      dependencies[packageName] = WEBFLOW_DEPENDENCIES_SET.has(packageName)
        ? ensureCaretRange(dep.version)
        : dep.version;
    });

    const packageJson: PackageJson = {
      name: 'webflow-site-components',
      private: true,
      dependencies,
    };

    return JSON.stringify(packageJson, null, 2);
  }
);

/**
 * Ensures a version string is in the caret (^) range format.
 *
 * @param version - The version string to ensure is in caret range format.
 * @returns The version string in caret range format.
 */
function ensureCaretRange(version: string): string {
  if (version.startsWith('^')) return version;
  const base = semver.valid(version.replace(/^[\^~]/, '').trim());
  return base ? `^${base}` : version;
}
